import { DiskSpaceCheck } from "@via/components/disk-space-check/disk-space-check.tsx";
import { DEFAULT_WORKSPACE_BASE, type ProfileClusterForClient } from "common";
import { type JSX } from "react";
import { type UseFormRegister } from "react-hook-form";

export const directoriesCardSummary = (params: {
  defWorkdir?: string | null;
  defPublishdir?: string | null;
}): string => {
  const parts = [
    params.defWorkdir?.trim(),
    params.defPublishdir?.trim(),
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "Defaults";
};

export function DirectoriesCard({
  register,
  showWorkspaceDirectory = false,
  diskSpace,
}: {
  register: UseFormRegister<ProfileClusterForClient>;
  /** agent-6-1: an agent environment's workspace directory (agent-d37). */
  showWorkspaceDirectory?: boolean;
  /**
   * The "Check disk space" buttons under the work and execution directories. The check runs on the saved
   * environment, so `runEnvironmentId` is null until it is saved. Left out, no button is shown.
   */
  diskSpace?: {
    runEnvironmentId: number | null;
    defNextflowWorkdir?: string | null;
    defWorkdir?: string | null;
  };
}): JSX.Element {
  const notSaved = diskSpace?.runEnvironmentId
    ? undefined
    : "Save the run environment first.";
  return (
    <>
      {showWorkspaceDirectory ? (
        <div>
          <label
            htmlFor="run-environment-workspace-base"
            className="block text-sm font-medium leading-6 text-gray-900">
            Workspace directory
          </label>
          <div className="mt-2">
            <input
              id="run-environment-workspace-base"
              {...register("workspaceBase")}
              type="text"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder={DEFAULT_WORKSPACE_BASE}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Where workspaces are created on the cluster, in each user&apos;s own
            home when it starts with ~. Leave it blank for{" "}
            {DEFAULT_WORKSPACE_BASE}. It is added to the environment&apos;s
            allowed folders, read and write.
          </p>
        </div>
      ) : null}

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Nextflow Path
        </label>
        <div className="mt-2">
          <input
            {...register("nextPath")}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="Your Nextflow Path"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Default Work Directory (Optional)
        </label>
        <div className="mt-2">
          <input
            {...register("defNextflowWorkdir")}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="Your Default Work Directory"
          />
        </div>
        {diskSpace ? (
          <DiskSpaceCheck
            testId="disk-space-work-directory"
            runEnvironmentId={diskSpace.runEnvironmentId}
            path={diskSpace.defNextflowWorkdir}
            disabledReason={notSaved}
          />
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Default Execution Directory
        </label>
        <div className="mt-2">
          <input
            {...register("defWorkdir")}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="Your Default Execution Directory"
          />
        </div>
        {diskSpace ? (
          <DiskSpaceCheck
            testId="disk-space-execution-directory"
            runEnvironmentId={diskSpace.runEnvironmentId}
            path={diskSpace.defWorkdir}
            disabledReason={notSaved}
          />
        ) : null}
        <p className="mt-1 text-xs text-gray-500">
          Leave a directory blank and the check reads your home directory.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Default Publish Directory
        </label>
        <div className="mt-2">
          <input
            {...register("defPublishdir")}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="Your Default Publish Directory"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Default Download Directory
        </label>
        <div className="mt-2">
          <input
            {...register("defDowndir")}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="Base path pipelines use to download reference data (params.DOWNDIR)"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Hide Execution Directory
        </label>
        <div>
          <input
            {...register("hideWorkdir")}
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
          />
        </div>
      </div>
    </>
  );
}
